if (!customElements.get("featured-blog-carousel")) {
  customElements.define(
    "featured-blog-carousel",
    class FeaturedBlogCarousel extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: ".featured-blog-carousel__items",
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        };
        this.classes = {
          grid: "f-grid",
          swiper: "swiper",
          swiperWrapper: "swiper-wrapper",
        };

        this.section = this.closest(".section");
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === "true";
        this.items = 3; // Always show 3 items on desktop
        this.tabletItems = 2; // Show 2 items on tablet
        this.mobileItems = 2; // Show 2 items on mobile
        this.swipeMobile = this.dataset.swipeMobile === "true";

        this.sliderInstance = false;

        if (!this.enableSlider) return;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);
        this.init();
      }

      init() {
        // Always initialize slider if enabled (for mobile, tablet, and desktop)
        if (this.enableSlider) {
          this.initSlider();
        } else {
          this.destroySlider();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === "object") return;
        const columnGap = window
          .getComputedStyle(this.sliderWrapper)
          .getPropertyValue("--f-column-gap");
        const spaceBetween = parseFloat(columnGap.replace("rem", "")) * 10;

        // Find navigation buttons in the section
        // On mobile, prioritize buttons in footer; on desktop, use header buttons
        const isMobile = window.innerWidth < 768;
        let nextButton = null;
        let prevButton = null;

        if (this.section) {
          if (isMobile) {
            // On mobile, look for buttons in footer first
            const footer = this.section.querySelector(
              ".featured-blog-carousel__footer"
            );
            if (footer) {
              nextButton = footer.querySelector(this.selectors.nextEl);
              prevButton = footer.querySelector(this.selectors.prevEl);
            }
            // Fallback to header if not found in footer
            if (!nextButton || !prevButton) {
              nextButton = this.section.querySelector(this.selectors.nextEl);
              prevButton = this.section.querySelector(this.selectors.prevEl);
            }
          } else {
            // On desktop/tablet, use header buttons
            nextButton = this.section.querySelector(this.selectors.nextEl);
            prevButton = this.section.querySelector(this.selectors.prevEl);
          }
        }

        // Fallback: try to find buttons in document
        if (!nextButton || !prevButton) {
          const sectionId = this.dataset.sectionId;
          const sectionElement = document.querySelector(
            `.section[data-section-id="${sectionId}"]`
          );
          if (sectionElement) {
            if (isMobile) {
              const footer = sectionElement.querySelector(
                ".featured-blog-carousel__footer"
              );
              if (footer) {
                nextButton =
                  nextButton || footer.querySelector(this.selectors.nextEl);
                prevButton =
                  prevButton || footer.querySelector(this.selectors.prevEl);
              }
            }
            if (!nextButton || !prevButton) {
              nextButton =
                nextButton ||
                sectionElement.querySelector(this.selectors.nextEl);
              prevButton =
                prevButton ||
                sectionElement.querySelector(this.selectors.prevEl);
            }
          }
        }

        if (!nextButton || !prevButton) {
          return;
        }

        // Check if mobile
        // const isMobile = window.innerWidth < 768;

        this.sliderOptions = {
          slidesPerView: this.mobileItems, // Show 2 items on mobile
          spaceBetween: spaceBetween,
          slidesPerGroup: this.mobileItems, // Move 2 slides at a time on mobile
          direction: "horizontal", // Always horizontal, CSS will handle vertical layout on mobile
          // On mobile, disable Swiper navigation - we'll handle it manually
          navigation: isMobile
            ? false
            : {
                nextEl: nextButton,
                prevEl: prevButton,
              },
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems,
              slidesPerGroup: 1, // Move 1 slide at a time on tablet
            },
            1280: {
              slidesPerView: this.items, // Always 3 on desktop
              slidesPerGroup: 3, // Move 3 slides at a time on desktop
            },
          },
          loop: false,
          threshold: 2,
          watchSlidesProgress: true,
          initialSlide: 0, // Start from first slide
        };

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(
          this,
          this.sliderOptions,
          []
        );
        // Check slides before init - look for both .swiper-slide and .f-column
        const slidesBeforeInit = this.sliderWrapper.querySelectorAll(
          ".swiper-slide, .f-column"
        );

        if (slidesBeforeInit.length === 0) {
          return;
        }

        // Ensure all slides have swiper-slide class
        slidesBeforeInit.forEach((slide) => {
          if (!slide.classList.contains("swiper-slide")) {
            slide.classList.add("swiper-slide");
          }
        });

        // Verify slides after adding class
        const slidesAfterClass =
          this.sliderWrapper.querySelectorAll(".swiper-slide");

        this.sliderInstance.init();

        // Wait a bit for Swiper to initialize
        setTimeout(() => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const slider = this.sliderInstance.slider;
            const isMobile = window.innerWidth < 768;

            // Check slides after init
            const slidesAfterInit =
              this.sliderWrapper.querySelectorAll(".swiper-slide");

            // Get slides from Swiper - ensure we have the latest
            let allSlides = Array.from(slider.slides || []);

            // If no slides found, try to reinitialize
            if (allSlides.length === 0 && slidesAfterInit.length > 0) {
              // Force Swiper to find slides
              slider.update();
              slider.updateSlides();
              slider.updateSlidesClasses();
              // Re-fetch slides after update
              allSlides = Array.from(slider.slides || []);
            }

            // On mobile, ensure only 2 slides are visible at a time
            if (isMobile && allSlides.length > 0) {
              // Reset to first slide if at the end
              if (slider.activeIndex >= allSlides.length - 1) {
                slider.slideTo(0, 0);
              }

              // Ensure navigation buttons are enabled - force remove all lock/disable states
              const enableNavigation = () => {
                if (nextButton) {
                  nextButton.classList.remove(
                    "swiper-button-lock",
                    "swiper-button-disabled"
                  );
                  nextButton.removeAttribute("disabled");
                  nextButton.removeAttribute("aria-disabled");
                  nextButton.setAttribute("tabindex", "0");
                  nextButton.style.display = "";
                  nextButton.style.visibility = "visible";
                  nextButton.style.opacity = "1";
                  nextButton.style.pointerEvents = "auto";
                }
                if (prevButton) {
                  prevButton.classList.remove(
                    "swiper-button-lock",
                    "swiper-button-disabled"
                  );
                  prevButton.removeAttribute("disabled");
                  prevButton.removeAttribute("aria-disabled");
                  prevButton.setAttribute("tabindex", "0");
                  prevButton.style.display = "";
                  prevButton.style.visibility = "visible";
                  prevButton.style.opacity = "1";
                  prevButton.style.pointerEvents = "auto";
                }
              };

              // Track current visible index manually
              let currentVisibleIndex = 0;
              const slidesToShow = 2;

              const updateVisibleSlides = () => {
                // Re-fetch slides to ensure we have the latest
                const latestSlides = Array.from(slider.slides || []);
                const slidesToUpdate =
                  latestSlides.length > 0 ? latestSlides : allSlides;

                slidesToUpdate.forEach((slide, index) => {
                  // Show only 2 slides starting from currentVisibleIndex
                  const shouldShow =
                    index >= currentVisibleIndex &&
                    index < currentVisibleIndex + slidesToShow;

                  if (shouldShow) {
                    // Show slide - use important to override any CSS and Swiper styles
                    slide.style.setProperty("display", "block", "important");
                    slide.style.setProperty(
                      "visibility",
                      "visible",
                      "important"
                    );
                    slide.style.setProperty("opacity", "1", "important");
                    slide.style.setProperty("height", "auto", "important");
                    slide.style.setProperty("max-height", "none", "important");
                    slide.style.setProperty(
                      "pointer-events",
                      "auto",
                      "important"
                    );
                    slide.style.setProperty("overflow", "visible", "important");
                    slide.style.setProperty("margin", "", "important");
                    slide.style.setProperty("padding", "", "important");
                    slide.classList.remove("swiper-slide-hidden");
                    // Force reflow
                    slide.offsetHeight;
                  } else {
                    // Hide slide but keep in DOM
                    slide.style.setProperty("display", "none", "important");
                    slide.style.setProperty(
                      "visibility",
                      "hidden",
                      "important"
                    );
                    slide.style.setProperty("opacity", "0", "important");
                    slide.style.setProperty(
                      "pointer-events",
                      "none",
                      "important"
                    );
                    slide.classList.add("swiper-slide-hidden");
                  }
                });

                // Update allSlides reference
                if (latestSlides.length > 0) {
                  allSlides.length = 0;
                  allSlides.push(...latestSlides);
                }

                // Enable/disable navigation buttons based on position
                if (nextButton && prevButton) {
                  const canGoNext =
                    currentVisibleIndex + slidesToShow < allSlides.length;
                  const canGoPrev = currentVisibleIndex > 0;

                  // Force enable next button if we can go next
                  if (canGoNext) {
                    nextButton.classList.remove(
                      "swiper-button-disabled",
                      "swiper-button-lock"
                    );
                    nextButton.removeAttribute("disabled");
                    nextButton.removeAttribute("aria-disabled");
                    nextButton.setAttribute("tabindex", "0");
                    nextButton.style.pointerEvents = "auto";
                    nextButton.style.cursor = "pointer";
                  } else {
                    nextButton.classList.add("swiper-button-disabled");
                    nextButton.setAttribute("disabled", "");
                    nextButton.setAttribute("aria-disabled", "true");
                    nextButton.setAttribute("tabindex", "-1");
                    nextButton.style.opacity = "0.5";
                    nextButton.style.cursor = "not-allowed";
                  }

                  // Force enable prev button if we can go prev
                  if (canGoPrev) {
                    prevButton.classList.remove(
                      "swiper-button-disabled",
                      "swiper-button-lock"
                    );
                    prevButton.removeAttribute("disabled");
                    prevButton.removeAttribute("aria-disabled");
                    prevButton.setAttribute("tabindex", "0");
                    prevButton.style.pointerEvents = "auto";
                    prevButton.style.cursor = "pointer";
                  } else {
                    prevButton.classList.add("swiper-button-disabled");
                    prevButton.setAttribute("disabled", "");
                    prevButton.setAttribute("aria-disabled", "true");
                    prevButton.setAttribute("tabindex", "-1");
                    prevButton.style.opacity = "0.5";
                    prevButton.style.cursor = "not-allowed";
                  }
                }
              };

              // Enable navigation buttons immediately
              enableNavigation();

              // Prevent Swiper from locking buttons
              const preventButtonLock = () => {
                if (
                  nextButton &&
                  nextButton.classList.contains("swiper-button-lock")
                ) {
                  nextButton.classList.remove("swiper-button-lock");
                  nextButton.removeAttribute("disabled");
                }
                if (
                  prevButton &&
                  prevButton.classList.contains("swiper-button-lock")
                ) {
                  prevButton.classList.remove("swiper-button-lock");
                  prevButton.removeAttribute("disabled");
                }
              };

              // Initial update
              setTimeout(() => {
                // Re-fetch allSlides to ensure we have the latest
                const latestSlides = Array.from(slider.slides || []);
                if (latestSlides.length > 0) {
                  allSlides.length = 0;
                  allSlides.push(...latestSlides);
                }
                updateVisibleSlides();
                enableNavigation();
                preventButtonLock();
              }, 150);

              // Sync Swiper's position with our manual index (if user swipes)
              slider.on("slideChange", () => {
                // If Swiper moved (e.g., via swipe), sync our index
                const swiperIndex =
                  slider.realIndex !== undefined
                    ? slider.realIndex
                    : slider.activeIndex || 0;
                // Round to nearest group of 2
                currentVisibleIndex =
                  Math.floor(swiperIndex / slidesToShow) * slidesToShow;
                updateVisibleSlides();
                enableNavigation();
                preventButtonLock();
              });

              // Update after slide change transition completes (more reliable)
              slider.on("slideChangeTransitionEnd", () => {
                setTimeout(() => {
                  const swiperIndex =
                    slider.realIndex !== undefined
                      ? slider.realIndex
                      : slider.activeIndex || 0;
                  currentVisibleIndex =
                    Math.floor(swiperIndex / slidesToShow) * slidesToShow;
                  updateVisibleSlides();
                  enableNavigation();
                  preventButtonLock();
                }, 10);
              });

              // Update on slides progress (when Swiper calculates visible slides)
              slider.on("slidesProgress", () => {
                updateVisibleSlides();
                enableNavigation();
                preventButtonLock();
              });

              // Also update on init
              slider.on("init", () => {
                setTimeout(() => {
                  updateVisibleSlides();
                  enableNavigation();
                  preventButtonLock();
                }, 100);
              });

              // Manual navigation handlers - update immediately
              const handleButtonClick = (e, direction) => {
                // Always prevent default behavior and stop propagation
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                // Store current scroll position immediately to prevent scroll
                const currentScrollY = window.scrollY || window.pageYOffset;

                // Prevent focus scroll by blurring immediately
                if (e.target && e.target.blur) {
                  e.target.blur();
                }

                const canNavigate =
                  direction === "next"
                    ? currentVisibleIndex + slidesToShow < allSlides.length
                    : currentVisibleIndex > 0;

                if (canNavigate) {
                  // Update index immediately
                  if (direction === "next") {
                    currentVisibleIndex += slidesToShow;
                    currentVisibleIndex = Math.min(
                      currentVisibleIndex,
                      allSlides.length - slidesToShow
                    );
                  } else {
                    currentVisibleIndex = Math.max(
                      0,
                      currentVisibleIndex - slidesToShow
                    );
                  }

                  // Use requestAnimationFrame for smooth update
                  requestAnimationFrame(() => {
                    // Update visible slides
                    updateVisibleSlides();

                    // Update Swiper position without animation on mobile to prevent flicker
                    slider.slideTo(currentVisibleIndex, 0);

                    // Update navigation state
                    enableNavigation();
                    preventButtonLock();

                    // Restore scroll position if it changed (prevent any scroll)
                    requestAnimationFrame(() => {
                      const newScrollY = window.scrollY || window.pageYOffset;
                      if (Math.abs(newScrollY - currentScrollY) > 5) {
                        window.scrollTo({
                          top: currentScrollY,
                          behavior: "instant",
                        });
                      }
                    });
                  });
                }

                return false;
              };

              if (nextButton) {
                // Remove any existing listeners by cloning
                const newNextButton = nextButton.cloneNode(true);
                nextButton.parentNode.replaceChild(newNextButton, nextButton);
                nextButton = newNextButton;

                nextButton.addEventListener(
                  "click",
                  (e) => handleButtonClick(e, "next"),
                  true
                ); // Use capture phase to intercept before Swiper
              }
              if (prevButton) {
                // Remove any existing listeners by cloning
                const newPrevButton = prevButton.cloneNode(true);
                prevButton.parentNode.replaceChild(newPrevButton, prevButton);
                prevButton = newPrevButton;

                prevButton.addEventListener(
                  "click",
                  (e) => handleButtonClick(e, "prev"),
                  true
                ); // Use capture phase to intercept before Swiper
              }

              // Continuously check and prevent button lock (Swiper may re-lock them)
              setInterval(() => {
                preventButtonLock();
              }, 500);
            }
          }
        }, 100);
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);
        if (typeof this.sliderInstance !== "object") return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  );
}
